import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  IoAddCircleOutline,
  IoCalendarOutline,
  IoDocumentTextOutline,
  IoOpenOutline,
  IoPersonOutline,
  IoRefreshOutline,
  IoRemoveCircleOutline,
  IoSearchOutline,
  IoTimeOutline,
} from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

import BaseLayout from '../../components/layout/BaseLayout';
import { DataState } from '../../context/DataProvider';
import {
  fetchSharePointChangeDetail,
  fetchSharePointChangeHistory,
} from '../../services/api';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date);
};

const timeAgo = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const diff = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const formatActor = (record, userInfo) =>
  record?.user_name ||
  record?.username ||
  record?.user_email ||
  record?.email ||
  userInfo?.name ||
  userInfo?.email ||
  record?.user_google_id ||
  'Current user';

const getRecordTitle = (record) =>
  record?.comment || record?.checkin_comment || 'Document update';

// Settings changes (site on/off, access, connect) are about no one document.
const getDocumentName = (record) =>
  record?.document_name ||
  record?.place_name ||
  (record?.operation?.startsWith('sharepoint_access')
    ? 'SharePoint settings'
    : record?.item_id || 'Untitled document');

const DiffView = ({ segments, hasRichDiff = true, note }) => {
  const safeSegments = segments || [];

  if (!safeSegments.length) {
    return (
      <div className="bg-2 br-1 flex min-h-48 items-center justify-center rounded-xl border p-6 text-center">
        <div>
          <IoDocumentTextOutline className="text-s-text mx-auto mb-3 text-3xl" />
          <p className="text-p-text text-sm font-medium">
            {note ||
              (hasRichDiff
                ? 'No diff content was stored for this change.'
                : 'Only SharePoint version metadata was captured for this record.')}
          </p>
          <p className="text-s-text mt-1 text-xs">
            {note
              ? 'Who, what and when are above.'
              : hasRichDiff
                ? 'Select another record or refresh the history.'
                : 'Future edits will include before/after text when the rich diff logger runs.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-2 br-1 max-h-[30rem] overflow-auto whitespace-pre-wrap rounded-xl border p-4 text-sm leading-6 text-zinc-200">
      {safeSegments.map((seg, index) => {
        if (seg.op === 'insert') {
          return (
            <ins
              key={index}
              className="rounded bg-emerald-500/20 px-0.5 text-emerald-200 no-underline"
            >
              {seg.text}
            </ins>
          );
        }
        if (seg.op === 'delete') {
          return (
            <del
              key={index}
              className="rounded bg-red-500/20 px-0.5 text-red-200"
            >
              {seg.text}
            </del>
          );
        }
        return <span key={index}>{seg.text}</span>;
      })}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, tone = 'text-[#a5a3ff]' }) => (
  <div className="bg-1 br-1 rounded-xl border p-4">
    <div className="flex items-center gap-3">
      <span className={`bg-2 rounded-lg p-2 ${tone}`}>
        <Icon className="text-lg" />
      </span>
      <div>
        <p className="text-s-text text-xs">{label}</p>
        <p className="text-p-text mt-0.5 text-lg font-bold">{value}</p>
      </div>
    </div>
  </div>
);

const DetailField = ({ label, value }) => (
  <div className="bg-2 br-1 min-w-0 rounded-lg border px-3 py-2">
    <p className="text-s-text text-[10px] font-semibold uppercase tracking-wide">
      {label}
    </p>
    <p className="text-p-text mt-1 truncate text-xs" title={value || 'N/A'}>
      {value || 'N/A'}
    </p>
  </div>
);

const TextSnapshot = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-s-text mb-2 text-xs font-semibold">{label}</p>
    <pre className="bg-2 br-1 max-h-60 overflow-auto whitespace-pre-wrap rounded-xl border p-3 text-xs leading-5 text-zinc-300">
      {value || 'No content captured.'}
    </pre>
  </div>
);

// `embedded`: shown in a window over Account Settings instead of as a page.
const DocumentChangeHistory = ({ userIdToken, embedded = false }) => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [filterItemId, setFilterItemId] = useState('');
  const [search, setSearch] = useState('');
  const { userInfo } = DataState();

  // Backend verify_jwt_token reads project_id from the project token.
  const token = localStorage.getItem('projectToken') || userIdToken;

  const openRecord = useCallback(
    async (recordId) => {
      if (!token || !recordId) return;
      setSelectedId(recordId);
      setIsLoadingDetail(true);
      try {
        const detail = await fetchSharePointChangeDetail(token, recordId);
        setSelected(detail);
      } catch (error) {
        toast.error(
          typeof error === 'string' ? error : 'Failed to load change detail.',
        );
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [token],
  );

  const loadRecords = useCallback(async () => {
    if (!token) {
      toast.error('Login token missing. Please login again.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await fetchSharePointChangeHistory(token, {
        item_id: filterItemId.trim() || undefined,
        limit: 100,
      });
      const list = result?.records || [];
      setRecords(list);

      if (list.length && !selectedId) {
        await openRecord(list[0]._id);
      } else if (!list.length) {
        setSelected(null);
        setSelectedId('');
      }
    } catch (error) {
      setRecords([]);
      setSelected(null);
      setSelectedId('');
      toast.error(
        typeof error === 'string' ? error : 'Failed to load document changes.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [filterItemId, openRecord, selectedId, token]);

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRecords = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;

    return records.filter((record) =>
      [
        getRecordTitle(record),
        getDocumentName(record),
        record?.item_id,
        record?.drive_id,
        record?.version_id,
        record?.source,
        formatActor(record, userInfo),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)),
    );
  }, [records, search, userInfo]);

  const stats = useMemo(
    () =>
      records.reduce(
        (acc, record) => {
          acc.added += record?.summary?.chars_added || 0;
          acc.removed += record?.summary?.chars_removed || 0;
          return acc;
        },
        { added: 0, removed: 0 },
      ),
    [records],
  );

  const selectedTitle = selected ? getRecordTitle(selected) : '';
  const selectedDocument = selected ? getDocumentName(selected) : '';

  const body = (
    <div
      className={`flex h-full min-h-0 flex-col gap-4 overflow-hidden ${
        // In the window, text takes the page's colour (BaseLayout's body
        // colour), not the window's.
        embedded ? 'p-text' : 'px-1 pt-8 sm:px-3 md:pt-10'
      }`}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        {/* The window's own title says what this is. */}
        <div className={embedded ? 'hidden' : ''}>
          <div className="flex items-center gap-2">
            <span className="rounded-lg bg-[#5652CF]/15 p-2 text-[#a5a3ff]">
              <IoDocumentTextOutline className="text-xl" />
            </span>
            <div>
              <h1 className="text-p-text text-2xl font-bold">
                Document Change History
              </h1>
              <p className="text-s-text mt-0.5 text-sm">
                See what changed, which document changed, who changed it, and
                when it happened.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative min-w-0 sm:w-72">
            <IoSearchOutline className="text-s-text pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search document, user, source"
              className="bg-2 br-1 text-p-text w-full rounded-lg border py-2 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-[#5652CF]"
            />
          </div>
          <button
            type="button"
            onClick={loadRecords}
            disabled={isLoading}
            className="flex items-center justify-center gap-2 rounded-lg bg-[#5652CF]/15 px-3 py-2 text-sm font-medium text-[#a5a3ff] transition-colors hover:bg-[#5652CF]/25 disabled:cursor-wait disabled:opacity-60"
          >
            <IoRefreshOutline
              className={isLoading ? 'animate-spin text-base' : 'text-base'}
            />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <StatCard
          icon={IoDocumentTextOutline}
          label="Tracked changes"
          value={records.length.toLocaleString()}
        />
        <StatCard
          icon={IoAddCircleOutline}
          label="Characters added"
          value={stats.added.toLocaleString()}
          tone="text-emerald-400"
        />
        <StatCard
          icon={IoRemoveCircleOutline}
          label="Characters removed"
          value={stats.removed.toLocaleString()}
          tone="text-red-400"
        />
      </div>

      <div className="bg-1 br-1 flex flex-col gap-3 rounded-xl border p-3 md:flex-row md:items-center">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label
            htmlFor="document-item-filter"
            className="text-s-text text-xs font-medium"
          >
            Filter by SharePoint item ID
          </label>
          <input
            id="document-item-filter"
            value={filterItemId}
            onChange={(event) => setFilterItemId(event.target.value)}
            placeholder="Optional exact item ID"
            className="bg-2 br-1 text-p-text rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#5652CF]"
          />
        </div>
        <button
          type="button"
          onClick={loadRecords}
          disabled={isLoading}
          className="rounded-lg bg-[#5652CF] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#4c47bd] disabled:cursor-wait disabled:opacity-60 md:self-end"
        >
          Apply Filter
        </button>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <section className="bg-1 br-1 flex min-h-0 flex-col overflow-hidden rounded-xl border">
          <div className="br-1 flex items-center justify-between border-b px-4 py-3">
            <div>
              <h2 className="text-p-text text-sm font-semibold">Changes</h2>
              <p className="text-s-text text-xs">
                Showing {filteredRecords.length} of {records.length}
              </p>
            </div>
            <span className="bg-2 br-1 rounded border px-2 py-1 text-xs text-zinc-300">
              Local audit log
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            {isLoading ? (
              <div className="space-y-2 p-3">
                {[...Array(6)].map((_, index) => (
                  <div
                    key={index}
                    className="bg-2 h-24 animate-pulse rounded-lg"
                  />
                ))}
              </div>
            ) : filteredRecords.length ? (
              <div className="divide-y divide-zinc-800">
                {filteredRecords.map((record) => {
                  const isActive = selectedId === record._id;
                  const added = record?.summary?.chars_added || 0;
                  const removed = record?.summary?.chars_removed || 0;
                  return (
                    <button
                      key={record._id}
                      type="button"
                      onClick={() => openRecord(record._id)}
                      className={`grid w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-800/60 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.75fr)_minmax(0,0.8fr)] ${
                        isActive ? 'bg-[#5652CF]/12' : 'bg-transparent'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="text-p-text truncate text-sm font-semibold">
                          {getRecordTitle(record)}
                        </p>
                        <p className="text-s-text mt-1 flex min-w-0 items-center gap-1 text-xs">
                          <IoDocumentTextOutline className="shrink-0" />
                          <span className="truncate">
                            {getDocumentName(record)}
                          </span>
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="rounded bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
                            +{added.toLocaleString()}
                          </span>
                          <span className="rounded bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-300">
                            -{removed.toLocaleString()}
                          </span>
                          {record.version_id ? (
                            <span className="bg-2 rounded px-2 py-0.5 text-[10px] text-zinc-400">
                              v{record.version_id}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="text-s-text min-w-0 text-xs">
                        <p className="flex items-center gap-1">
                          <IoPersonOutline className="shrink-0" />
                          <span className="truncate">
                            {formatActor(record, userInfo)}
                          </span>
                        </p>
                        <p className="mt-1 truncate capitalize">
                          {record.source || 'sharepoint_or_onedrive'}
                        </p>
                      </div>

                      <div className="text-s-text min-w-0 text-xs">
                        <p className="flex items-center gap-1">
                          <IoCalendarOutline className="shrink-0" />
                          <span className="truncate">
                            {formatDate(record.created_at)}
                          </span>
                        </p>
                        <p className="mt-1 flex items-center gap-1">
                          <IoTimeOutline className="shrink-0" />
                          <span>{timeAgo(record.created_at)}</span>
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex min-h-80 items-center justify-center p-6 text-center">
                <div>
                  <IoDocumentTextOutline className="text-s-text mx-auto mb-3 text-4xl" />
                  <p className="text-p-text font-medium">
                    No document changes found
                  </p>
                  <p className="text-s-text mt-1 text-sm">
                    Try clearing the filters or refresh after a SharePoint
                    document is updated from chat.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="bg-1 br-1 flex min-h-0 flex-col overflow-hidden rounded-xl border">
          <div className="br-1 flex items-start justify-between gap-3 border-b px-4 py-3">
            <div className="min-w-0">
              <h2 className="text-p-text truncate text-sm font-semibold">
                {selected ? selectedTitle : 'Change detail'}
              </h2>
              <p className="text-s-text mt-1 truncate text-xs">
                {selected
                  ? selectedDocument
                  : 'Select a change to inspect the exact edit.'}
              </p>
            </div>
            {selected?.document_web_url ? (
              <a
                href={selected.document_web_url}
                target="_blank"
                rel="noreferrer"
                className="flex shrink-0 items-center gap-2 rounded-lg border border-[#5652CF]/30 bg-[#5652CF]/10 px-3 py-2 text-xs font-semibold text-[#a5a3ff] transition-colors hover:bg-[#5652CF]/20"
              >
                <IoOpenOutline />
                Open
              </a>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-4">
            {isLoadingDetail ? (
              <div className="space-y-3">
                <div className="bg-2 h-24 animate-pulse rounded-xl" />
                <div className="bg-2 h-80 animate-pulse rounded-xl" />
              </div>
            ) : selected ? (
              <div className="space-y-4">
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  <DetailField
                    label="Who"
                    value={formatActor(selected, userInfo)}
                  />
                  <DetailField
                    label="When"
                    value={formatDate(selected.created_at)}
                  />
                  <DetailField label="Source" value={selected.source} />
                  <DetailField label="Version" value={selected.version_id} />
                  <DetailField label="Item ID" value={selected.item_id} />
                  <DetailField label="Task ID" value={selected.task_id} />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4">
                    <p className="text-xs font-semibold text-emerald-300">
                      Added
                    </p>
                    <p className="mt-1 text-2xl font-bold text-emerald-200">
                      +{(selected.summary?.chars_added || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4">
                    <p className="text-xs font-semibold text-red-300">
                      Removed
                    </p>
                    <p className="mt-1 text-2xl font-bold text-red-200">
                      -{(selected.summary?.chars_removed || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-p-text text-sm font-semibold">
                      Inline Diff
                    </h3>
                    <p className="text-s-text text-xs">
                      Green is added, red is removed
                    </p>
                  </div>
                  <DiffView
                    segments={selected.diff_segments}
                    hasRichDiff={
                      selected.summary?.rich_diff_available !== false
                    }
                    note={selected.summary?.note}
                  />
                </div>

                <div className="grid gap-3 lg:grid-cols-2">
                  <TextSnapshot label="Before" value={selected.before_text} />
                  <TextSnapshot label="After" value={selected.after_text} />
                </div>
              </div>
            ) : (
              <div className="flex min-h-96 items-center justify-center text-center">
                <div>
                  <IoSearchOutline className="text-s-text mx-auto mb-3 text-4xl" />
                  <p className="text-p-text font-medium">
                    Select a change from the list
                  </p>
                  <p className="text-s-text mt-1 text-sm">
                    The detail panel will show metadata, the diff, and
                    before/after text.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );

  if (embedded) return body;
  return (
    <BaseLayout
      createName="New Chat"
      handleCreate={() => navigate('/chat')}
      tabName="document-history"
    >
      {body}
    </BaseLayout>
  );
};

export default DocumentChangeHistory;
